import type { Region } from '@/shared/utils/regions';
import type { WeightUnit } from '@/shared/types/api/packages';

export type AllocField = { product_id: string; quantity: number };

export type TargetForm = {
  serverId?: string;
  pinfl: string;
  first_name: string;
  last_name: string;
  middle_name: string;
  phone_number: string;
  passport_series: string;
  address_name: string;
  region: Region;
  city: string;
  street: string;
  building: string;
  apartment: string;
  additional_info: string;
  package_name: string;
  /** Optional admin-entered package price. Empty string means "compute from weight". */
  package_price: number | string;
  package_weight: number | string;
  package_weight_unit: WeightUnit;
  /**
   * Snapshot of an already-approved target's package (price + the weight it
   * was priced at). While the weight is unchanged and the price field is left
   * blank, the backend KEEPS this price on re-save instead of re-pricing at
   * today's per-kg rate, so the placeholder must show it too.
   */
  saved_package?: { price: number; weight: string; unit: WeightUnit } | null;
  allocations: AllocField[];
};

export type ProductForm = {
  id: string;
  full_name: string;
  quantity: number | string;
  price_per_item: number | string;
};

export type FormValues = {
  products: ProductForm[];
  targets: TargetForm[];
};

// ---- Submit-time field validation --------------------------------------
// Numeric inputs may hold any partial / empty value while the admin types;
// nothing is clamped on change. These checks run on Save and drive the red
// borders (they re-run on every render after a Save attempt, so each error
// clears as soon as the field is fixed).
const isBlank = (v: unknown): boolean => String(v ?? '').trim() === '';
const toNum = (v: unknown): number => Number(String(v ?? '').replace(',', '.').trim());

/** Product quantity: required integer >= 1 (mirrors the backend rule). */
export const productQtyInvalid = (v: unknown): boolean => {
  if (isBlank(v)) return true;
  const n = toNum(v);
  return !Number.isInteger(n) || n < 1;
};

/** Product unit price: required number > 0 (mirrors the backend rule). */
export const productPriceInvalid = (v: unknown): boolean => {
  if (isBlank(v)) return true;
  const n = toNum(v);
  return !Number.isFinite(n) || n <= 0;
};

/** Allocation quantity: blank counts as 0; otherwise an integer >= 0. */
export const allocQtyInvalid = (v: unknown): boolean => {
  if (isBlank(v)) return false;
  const n = toNum(v);
  return !Number.isInteger(n) || n < 0;
};
